import {getUser} from "./util-auth";
import {
    COMPANY_BULLSEYE,
    COMPANY_DEV_TEST,
    COMPANY_NT_COMPANY,
    COMPANY_DB,
    COMPANY_NATIONWIDE_BROKERAGE,
    COMPANY_DEV_SLVWZ,
    COMPANY_AAM,
    COMPANY_FREIGHT_HOUSE
} from "../../util/util-constants";
import {getLookup} from "./util-helpers";

const SETTINGS_EXCEPTIONS = {
    // Load page - pre-select LoadTypeID on load create
    LoadInfoLoadTypeID: {
        [COMPANY_DB]: "1", // TL
        [COMPANY_BULLSEYE]: "1",
        ['dev_test']: "1"
    },

    // Load page - pre-select LoadSubTypeID on load create
    LoadInfoLoadSubTypeID: {
        [COMPANY_DB]: getLoadSubType(), // Van 53ft
        [COMPANY_BULLSEYE]: getLoadSubType(), // Van 53ft
        ['dev_test']: getLoadSubType()
    },

    // Truck page - pre-select TruckOwnershipTypeID on truck create
    TruckOwnershipTypeID: {
        [COMPANY_DB]: ['empty'],
        [COMPANY_BULLSEYE]: ['empty'],
        ['dev_test']: ['empty']
    },

    // Trailer page - pre-select TrailerOwnershipTypeID on trailer create
    TrailerOwnershipTypeID: {
        [COMPANY_DB]: ['empty'],
        [COMPANY_BULLSEYE]: ['empty'],
        ['dev_test']: ['empty']
    },

    // Loads page - hide the commission amount column in the load list
    HideCommisionAmount: {
        [COMPANY_DB]: true,
        [COMPANY_BULLSEYE]: true,
        ['dev_test']: true
    },

    // Loads page - hide the dispatch totals section on load listing
    HideDispatchTotalsONLoadListing: {
        [COMPANY_DB]: false,
        [COMPANY_BULLSEYE]: false,
        ['dev_test']: false
    },

    // Dispatch reports - hide the "Booked By" tab Excel export button
    HideDispatchReportsBookedByTabExcel: {
        [COMPANY_DB]: false,
        [COMPANY_BULLSEYE]: false,
        ['dev_test']: false
    },

    // Carriers page - disable the FMCSA import button
    CarriersImportFMCSADisabled: {
        [COMPANY_DB]: true,
        [COMPANY_BULLSEYE]: true,
        ['dev_test']: true
    },

    // App global - override the default toast notification position
    DefaultNotificationPosition: {
        [COMPANY_NT_COMPANY]: "bottom-right",
        ['dev_test']: "bottom-right"
    },

    // Controls visibility of the CoveredBy contact selector on the load edit view (load-view/index.jsx)
    // and the CoveredGrossReport / DriverMileage tabs in dispatch reports (reports-view/index.jsx).
    // Set to false for companies that do not use the "Covered By" assignment workflow.
    VisibleCoveredByAndDriverMileage: {
        [COMPANY_DB]: false,
        [COMPANY_BULLSEYE]: false,
        // [COMPANY_DEV_SLVWZ]: false,
        ['dev_test']: false
    },

    // Load page - show or hide the Rate Confirmation digital sign button
    VisibleShowRateDigitalSign: {
        [COMPANY_DB]: false,
        [COMPANY_BULLSEYE]: false,
        ['dev_test']: false
    },

    // Documents tab - auto-check IsSendWithInvoice and IsVisibleForCustomer when a POD or Rate Confirmation
    // document type is selected. Disabled for COMPANY_NATIONWIDE_BROKERAGE because they do not want
    // these checkboxes pre-selected automatically.
    AutoCheckPODDocumentCheckboxes: {
        [COMPANY_NATIONWIDE_BROKERAGE]: false,
        ['dev_test']: false
    },

    // Load create page - pre-select the logged-in user's Office in the OfficeID field on mount.
    // Enabled for SLVWZ so dispatchers don't have to manually pick their office every time.
    LoadInfoOfficeIDPreFill: {
        [COMPANY_DEV_SLVWZ]: true,
        ['dev_test']: true
    },

    // Load create page - pre-select the logged-in user's primary ContactGroup in the ContactGroupID field on mount.
    // Enabled for SLVWZ so dispatchers don't have to manually pick their contact group every time.
    LoadInfoContactGroupIDPreFill: {
        [COMPANY_DEV_SLVWZ]: true,
        ['dev_test']: true
    },

    // Documents tab - include load reference numbers in auto-generated document file names
    LoadDocumentsLoadNumbersInName: {
        [COMPANY_DEV_SLVWZ]: true,
        ['dev_test']: true
    },

    // Load info tab - when user changes the Customer field, skip auto-filling OfficeID from customer metadata.
    // Disabled for COMPANY_DEV_SLVWZ because they manage office assignment independently of customer.
    LoadInfoOfficeIDAutoFillOnCustomerChange: {
        [COMPANY_DEV_SLVWZ]: false,
        ['dev_test']: false
    },

    // Load info tab - default value for OfficeID on load create when no pre-fill logic applies.
    // Empty string for AAM and DB means the field is left blank and the user must select manually.
    OfficeIdDefaultValue: {
        [COMPANY_AAM]: "",
        [COMPANY_DB]: ""
    },

    // Accounting - suffix appended to the Triumph Factoring primary integration button label/tooltip.
    // Lets each company show their own factoring account name in the UI.
    TriumphPrimaryName: {
        [COMPANY_AAM]: " (AAM Network)",
        [COMPANY_DEV_SLVWZ]: " (Sunny Way)"
    },

    // Accounting - suffix for the Triumph Factoring secondary integration button label/tooltip.
    TriumphSecondaryName: {
        [COMPANY_AAM]: " (AlphaAndMike)",
        [COMPANY_DEV_SLVWZ]: " (Astra Group)"
    },

    // Accounting - suffix for the Triumph Factoring tertiary integration button label/tooltip.
    TriumphTertiaryName: {
        [COMPANY_AAM]: ""
    },

    // Load info tab - number of visible rows in the External Carrier Notes textarea.
    // Increased for NATIONWIDE_BROKERAGE because they enter longer notes by default.
    ExternalCarrierNotesRows: {
        [COMPANY_NATIONWIDE_BROKERAGE]: 14
    },

    // Load info tab - whether the External Carrier Notes field is expanded by default when creating a load.
    // Enabled for NATIONWIDE_BROKERAGE so the field is always visible without manual expansion.
    isCarrierNotesExpandedByDefault: {
        [COMPANY_NATIONWIDE_BROKERAGE]: true
    },

    // Load view - disable the "Send Rate Confirmation by email" button.
    // Disabled for FREIGHT_HOUSE because they send rate confirmations through a separate workflow.
    isSendEmailRateConfirmationDisabled: {
        [COMPANY_FREIGHT_HOUSE]: true
    }
};

export default function CompanyExceptions(settingKey, defaultValue) {
    let companyID = getUser('CompanyID');

    if ((import.meta.env.VITE_ENV_MODE === "dev" || import.meta.env.VITE_ENV_MODE === "local") && companyID === COMPANY_DEV_TEST) {
        companyID = 'dev_test';
    }

    return SETTINGS_EXCEPTIONS[settingKey]?.[companyID] ?? defaultValue;
}
// Looks up a per-company override for settingKey. Returns the company-specific value if one exists,
// or defaultValue if the current company has no override. In dev/local mode, COMPANY_DEV_TEST maps
// to the 'dev_test' key so developers can test overrides without using a real company account.
//
// Known setting interactions / conflicts:
//
// 1. SLVWZ — OfficeID on load create (LoadInfoOfficeIDPreFill + LoadInfoOfficeIDAutoFillOnCustomerChange)
//    LoadInfoOfficeIDPreFill=true pre-fills OfficeID with the dispatcher's own office on mount.
//    LoadInfoOfficeIDAutoFillOnCustomerChange=false prevents that value from being overwritten when
//    the customer is changed. Both must stay aligned for SLVWZ — enabling one without the other
//    would either leave the field blank or silently overwrite the dispatcher's office on customer pick.
//
// 2. AAM / DB — OfficeIdDefaultValue="" makes LoadInfoOfficeIDAutoFillOnCustomerChange unreachable
//    The condition in load-info-tab.jsx that triggers OfficeID auto-fill on customer change checks
//    CompanyExceptions('OfficeIdDefaultValue', null) !== '' first. For AAM and DB this evaluates
//    to false and short-circuits, so LoadInfoOfficeIDAutoFillOnCustomerChange is never evaluated
//    for those companies. To change OfficeID auto-fill behaviour for AAM/DB, update OfficeIdDefaultValue,
//    not LoadInfoOfficeIDAutoFillOnCustomerChange.
//
// 3. TriumphTertiaryName for COMPANY_AAM is "" — same as the default callers pass
//    All call sites use CompanyExceptions('TriumphTertiaryName', ""). The AAM override is also "",
//    so it has no visible effect. The entry exists as a placeholder in case AAM ever adds a tertiary
//    Triumph account and needs a label — update the value then.


function getLoadSubType() {
    const types = getLookup('LoadSubType');
    const entry = Object.entries(types ?? {}).find(([, val]) => val.toLowerCase() === 'van 53ft');
    return entry ? entry[0] : '';
}
// Resolves the lookup ID for the "Van 53ft" load sub-type at module load time.
// Used as a default value in LoadInfoLoadSubTypeID so the correct option is
// pre-selected without hard-coding a numeric ID that may differ per environment.


//   Test cases per scenario:
//
// 1. SLVWZ — create page
// - OfficeID is pre-filled with the logged-in user's office on mount
// - ContactGroupID is pre-filled with the user's primary contact group on mount
// - Both fields are editable (user can clear/change them)
// - Saving the load persists the pre-selected values
// 2. AAM / DB — create page
// - OfficeID is blank on mount (user must pick manually)
// - ContactGroupID is blank on mount
// - No regression from before
// 3. Any other company — create page
// - OfficeID pre-selects user's office (unchanged default behavior)
// - ContactGroupID is blank
// 4. All companies — edit page
// - OfficeID shows the load's saved office (not the user's office)
// - ContactGroupID shows the load's saved group (or blank if none)
// - isCreatePage path is not triggered — verify no regression
